import { DateTime } from "luxon";
import type { AffectedAppointment } from "../api/types.js";
import { Card } from "./Card.js";

type Props = {
  affected: readonly AffectedAppointment[];
  // Practice timezone (from the calendar-config query) — the appointment
  // times below are shown in local practice time, not the browser's.
  timezone: string;
  forcing: boolean;
  onKeep: () => void;
  onForce: () => void;
};

function formatLocalRange(startIso: string, endIso: string, tz: string): string {
  const start = DateTime.fromISO(startIso).setZone(tz);
  const end = DateTime.fromISO(endIso).setZone(tz);
  return `${start.toFormat("ccc, LLL d, h:mm a")} – ${end.toFormat("h:mm a")}`;
}

// Shown when a shift/block edit or delete would strand already-booked
// appointments (API 409 `appointments_affected`) — on eventDrop/eventResize
// (Task 6 behavior 4/5) and on Delete. Never destructive on its own: the
// edit/delete has already been reverted/refused before this renders.
export function AffectedApptsDialog({
  affected,
  timezone,
  forcing,
  onKeep,
  onForce,
}: Props) {
  return (
    <div
      role="dialog"
      aria-label="Appointments affected"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <Card title="Appointments affected" className="w-full max-w-md">
        <p className="mb-3 text-sm text-slate-700">
          These booked appointments fall outside the new hours. Nothing has
          been cancelled.
        </p>
        <ul className="mb-4 grid gap-2">
          {affected.map((a) => (
            <li
              key={a.id}
              className="rounded-md border border-slate-200 px-3 py-2 text-sm"
            >
              <span className="font-mono font-semibold uppercase">
                {a.public_code}
              </span>{" "}
              <span className="text-slate-600">
                {formatLocalRange(a.scheduled_start, a.scheduled_end, timezone)}
              </span>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onKeep}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50"
          >
            Keep schedule as-is
          </button>
          <button
            type="button"
            onClick={onForce}
            disabled={forcing}
            className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
          >
            {forcing ? "Changing…" : "Change anyway"}
          </button>
        </div>
      </Card>
    </div>
  );
}
